import {match} from 'ts-pattern';

import {always, clone, defaultTo, isEmpty} from 'ramda';

import {
  PutSupplierOrderItemCalculationResponse,
  SupplierOrderItemChangedField,
  usePutSupplierOrderItemCalculationMutation,
} from '@dms/api/warehouse';
import {useFormRecalculation} from '@dms/warehouse/shared-components';

import {sanitizeObject} from 'shared/web';

import {SupplierOrderItemForm} from '../../types/SupplierOrderItemForm';

interface UseSupplierOrderItemFormRecalculationConfig {
  supplierOrderItemId: string;
}

/**
 * Custom hook for handling supplier order item form recalculation with proper type handling
 * for the supplier order item calculation mutation. This is a specialized wrapper around the
 * generic useFormRecalculation hook.
 */
export function useSupplierOrderItemFormRecalculation(
  config: UseSupplierOrderItemFormRecalculationConfig
) {
  const [recalculateSupplierOrderItemPrices, {isLoading: isRecalculateLoading}] =
    usePutSupplierOrderItemCalculationMutation();

  const formRecalculation = useFormRecalculation<
    SupplierOrderItemForm,
    {
      changedField: {field: SupplierOrderItemChangedField};
      orderDetail: {
        supplierQuantity: number | undefined;
        supplierUnitPrice: number | undefined;
      };
      requestDetail: {
        warehouseRequestedQuantity: number | undefined;
        warehouseUnitPurchasePrice: number | undefined;
      };
      supplierArticle: {
        supplierQuantity: number | undefined;
        warehouseQuantity: number | undefined;
      };
    },
    PutSupplierOrderItemCalculationResponse
  >({
    allowedFields: ALLOWED_OBJECTS_KEYS,
    allowedObjects: ALLOWED_OBJECTS,
    recalculateMutation: (params) => ({
      unwrap: () =>
        recalculateSupplierOrderItemPrices({
          orderItemId: config.supplierOrderItemId,
          body: params.body,
          signal: params.signal,
        }).unwrap(),
    }),
    requestConfig: {
      orderItemId: config.supplierOrderItemId,
    },
    buildRequestBody: (values, changedField) => ({
      changedField: {field: changedField as SupplierOrderItemChangedField},
      orderDetail: {
        supplierQuantity: values.orderDetail?.supplierQuantity,
        supplierUnitPrice: values.orderDetail?.supplierUnitPrice,
      },
      requestDetail: {
        warehouseRequestedQuantity: values.requestDetail?.warehouseRequestedQuantity,
        warehouseUnitPurchasePrice: values.requestDetail?.warehouseUnitPurchasePrice,
      },
      supplierArticle: {
        supplierQuantity: values.supplierArticle?.supplierQuantity,
        warehouseQuantity: values.supplierArticle?.warehouseQuantity,
      },
    }),
    // eslint-disable-next-line complexity -- Pre-existing violation
    transformResponse: (response, values) =>
      clone(
        sanitizeObject({
          orderDetail: {
            supplierQuantity: response.orderDetail.supplierQuantity,
            supplierUnit: values?.orderDetail?.supplierUnit,
            supplierUnitPrice: response.orderDetail.supplierUnitPrice,
            supplierTotalPrice: response.orderDetail.supplierTotalPrice,
            name: values?.orderDetail?.name,
            catalogueNumber: values?.orderDetail?.catalogueNumber,
            manufacturerId: values?.orderDetail?.manufacturerId,
            supplierOrderingNumber: values?.orderDetail?.supplierOrderingNumber,
            vat: defaultTo('S', values.orderDetail?.vat),
            vin: values?.orderDetail?.vin,
            mileage: values?.orderDetail?.mileage,
            keyCode: values?.orderDetail?.keyCode,
            isIntelligentPart: values?.orderDetail?.isIntelligentPart,
            engineNumber: values?.orderDetail?.engineNumber,
            labelInfo: values?.orderDetail?.labelInfo,
            serialNumber: values?.orderDetail?.serialNumber,
            note: values?.orderDetail?.note,
            location: values?.orderDetail?.location,
            engineCode: values?.orderDetail?.engineCode,
          },
          requestDetail: {
            warehouseRequestedQuantity: response.requestDetail.warehouseRequestedQuantity,
            warehouseUnitPurchasePrice: response.requestDetail.warehouseUnitPurchasePrice,
          },
          supplierArticle: {
            supplierQuantity: response.supplierArticle.supplierQuantity,
            supplierUnitId: values.supplierArticle?.supplierUnitId,
            warehouseQuantity: response.supplierArticle.warehouseQuantity,
            warehouseUnitId: values.supplierArticle?.warehouseUnitId,
            supplierOrderingNumber: values.supplierArticle?.supplierOrderingNumber,
            supplierBulkPackageQuantity: values.supplierArticle?.supplierBulkPackageQuantity,
            isUpdateMapping: values.supplierArticle?.isUpdateMapping ?? false,
          },
        })
      ),
    isDifferenceEmpty: (allowedDifference) => {
      const isOrderDetailDiffEmpty = allowedDifference?.orderDetail
        ? isEmpty(allowedDifference?.orderDetail)
        : false;
      const isRequestDetailDiffEmpty = allowedDifference?.requestDetail
        ? isEmpty(allowedDifference?.requestDetail)
        : false;
      const isSupplierArticleDiffEmpty = allowedDifference?.supplierArticle
        ? isEmpty(allowedDifference?.supplierArticle)
        : false;

      return isOrderDetailDiffEmpty && isRequestDetailDiffEmpty && isSupplierArticleDiffEmpty;
    },
    // supplierArticle.supplierQuantity/warehouseQuantity share their leaf name with orderDetail
    // fields, so the full path decides which BE field actually changed
    mapFieldName: (changedField, changedFieldWithFullPath) =>
      match(changedFieldWithFullPath)
        .with('supplierArticle.supplierQuantity', always('supplierQuantityMapping'))
        .with('supplierArticle.warehouseQuantity', always('warehouseQuantityMapping'))
        .otherwise(always(changedField)),
  });

  return {
    ...formRecalculation,
    isRecalculateLoading,
  };
}

const ALLOWED_OBJECTS = ['orderDetail', 'requestDetail', 'supplierArticle'];

// Nested keys from ALLOWED_OBJECTS
const ALLOWED_OBJECTS_KEYS = [
  'supplierQuantity',
  'supplierUnitPrice',
  'warehouseRequestedQuantity',
  'warehouseQuantity',
];
